import React, { useState, useEffect } from "react";
import { terminalAPI } from "../../services/api";
import { usePOSTerminal } from "../../context/POSContext";
import "./POSReaderStatus.css";

const POSReaderStatus = () => {
  // Get reader and location from context
  const { selectedReader: reader, selectedLocation: location } =
    usePOSTerminal();

  const [batteryLevel, setBatteryLevel] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [statusUpdateError, setStatusUpdateError] = useState(null);

  // Simulate reader status updates for S700 readers
  useEffect(() => {
    if (reader && reader.dbInfo && reader.dbInfo.status === "online") {
      // Only simulate battery updates for S700 readers
      const isS700 = reader.dbInfo && reader.dbInfo.model === "S700";

      if (isS700) {
        // Simulate battery level changes
        const batteryInterval = setInterval(() => {
          // Random battery level between 20 and 100
          const newBatteryLevel = Math.floor(Math.random() * 81) + 20;
          setBatteryLevel(newBatteryLevel);

          // Update reader status in the backend
          updateReaderStatus(newBatteryLevel);
        }, 60000); // Update every minute

        return () => clearInterval(batteryInterval);
      }
    }
  }, [reader]);

  // Function to update reader status
  const updateReaderStatus = async (newBatteryLevel) => {
    if (!reader) return;

    // Get the correct reader ID - for registered readers, it might be in reader.readerId or reader.id
    const readerId =
      reader.readerId || (reader.dbInfo && reader.dbInfo.readerId) || reader.id;

    if (!readerId) return;

    try {
      setIsUpdatingStatus(true);
      const response = await terminalAPI.updateReaderStatus({
        readerId: readerId,
        batteryLevel: newBatteryLevel,
        status: "online",
      });

      // Check if the response contains a reader that needs registration
      if (
        response.data &&
        response.data.reader &&
        response.data.reader.needsRegistration
      ) {
        setStatusUpdateError(
          `${response.data.message || "Reader needs registration."} ${
            response.data.suggestion || ""
          }`
        );
      } else {
        setStatusUpdateError(null);
      }
    } catch (error) {
      console.error("Error updating reader status:", error);
      setStatusUpdateError("Failed to update reader status");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Function to manually refresh reader status
  const handleRefreshStatus = async () => {
    if (!reader) return;

    // Get the correct reader ID - for registered readers, it might be in reader.readerId or reader.id
    const readerId =
      reader.readerId || (reader.dbInfo && reader.dbInfo.readerId) || reader.id;

    if (!readerId) return;

    try {
      setIsUpdatingStatus(true);
      const response = await terminalAPI.updateReaderStatus({
        readerId: readerId,
        status: "online",
        lastConnected: new Date(),
      });

      // Check if the response contains a reader that needs registration
      if (
        response.data &&
        response.data.reader &&
        response.data.reader.needsRegistration
      ) {
        setStatusUpdateError(
          `${response.data.message || "Reader needs registration."} ${
            response.data.suggestion || ""
          }`
        );
      } else {
        setStatusUpdateError(null);
      }
    } catch (error) {
      console.error("Error refreshing reader status:", error);
      setStatusUpdateError("Failed to refresh reader status");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  if (!reader || !location) {
    return (
      <div className="pos-reader-status pos-reader-not-selected">
        <div className="pos-reader-status-icon">
          <i className="fas fa-exclamation-triangle"></i>
        </div>
        <div className="pos-reader-status-text">
          <h3>No Reader Selected</h3>
          <p>Please select a location and reader to process payments</p>
        </div>
      </div>
    );
  }

  // Determine reader status
  const status = reader.dbInfo ? reader.dbInfo.status : "unknown";
  let statusClass = "pos-reader-unknown";
  let statusText = "Unknown";

  switch (status) {
    case "online":
      statusClass = "pos-reader-online";
      statusText = "Online";
      break;
    case "offline":
      statusClass = "pos-reader-offline";
      statusText = "Offline";
      break;
    case "not_registered":
      statusClass = "pos-reader-not-registered";
      statusText = "Not Registered";
      break;
    default:
      statusClass = "pos-reader-unknown";
      statusText = "Unknown";
  }

  // Format date for last transaction
  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const lastTransaction = reader.dbInfo ? reader.dbInfo.lastTransaction : null;
  const lastConnected = reader.dbInfo ? reader.dbInfo.lastConnected : null;

  // Get reader model
  const readerModel =
    reader.dbInfo && reader.dbInfo.model ? reader.dbInfo.model : "Unknown";

  // Get battery level
  const currentBatteryLevel =
    batteryLevel ||
    (reader.dbInfo && reader.dbInfo.batteryLevel
      ? reader.dbInfo.batteryLevel
      : null);

  // Get firmware version
  const firmwareVersion =
    reader.dbInfo && reader.dbInfo.firmwareVersion
      ? reader.dbInfo.firmwareVersion
      : "Unknown";

  return (
    <div className={`pos-reader-status ${statusClass}`}>
      <div className="pos-reader-status-header">
        <div className="pos-reader-status-icon">
          <i className="fas fa-credit-card"></i>
        </div>
        <div className="pos-reader-status-text">
          <h3>{reader.label || "Card Reader"}</h3>
          <p className="pos-reader-location">{location.display_name}</p>
        </div>
        <div className="pos-reader-status-badge">
          <span>{statusText}</span>
          {isUpdatingStatus && (
            <span className="pos-reader-updating"> (Updating...)</span>
          )}
        </div>
      </div>

      {statusUpdateError && (
        <div className="pos-reader-error">{statusUpdateError}</div>
      )}

      <div className="pos-reader-details">
        <div className="pos-reader-detail">
          <span className="pos-reader-detail-label">Reader ID:</span>
          <span className="pos-reader-detail-value">{reader.id}</span>
        </div>
        <div className="pos-reader-detail">
          <span className="pos-reader-detail-label">Model:</span>
          <span className="pos-reader-detail-value">{readerModel}</span>
        </div>
        {readerModel === "S700" && currentBatteryLevel !== null && (
          <div className="pos-reader-detail">
            <span className="pos-reader-detail-label">Battery:</span>
            <span className="pos-reader-detail-value">
              <div className="pos-reader-battery">
                <div
                  className={`pos-reader-battery-level ${
                    currentBatteryLevel < 20 ? "low" : ""
                  }`}
                  style={{ width: `${currentBatteryLevel}%` }}
                ></div>
              </div>
              {currentBatteryLevel}%
            </span>
          </div>
        )}
        {readerModel === "S700" && firmwareVersion !== "Unknown" && (
          <div className="pos-reader-detail">
            <span className="pos-reader-detail-label">Firmware:</span>
            <span className="pos-reader-detail-value">{firmwareVersion}</span>
          </div>
        )}
        <div className="pos-reader-detail">
          <span className="pos-reader-detail-label">Last Transaction:</span>
          <span className="pos-reader-detail-value">
            {formatDate(lastTransaction)}
          </span>
        </div>
        <div className="pos-reader-detail">
          <span className="pos-reader-detail-label">Last Connected:</span>
          <span className="pos-reader-detail-value">
            {formatDate(lastConnected)}
          </span>
        </div>
      </div>

      <div className="pos-reader-actions">
        <button
          className="pos-reader-refresh-btn"
          onClick={handleRefreshStatus}
          disabled={isUpdatingStatus}
        >
          <i className="fas fa-sync-alt"></i> Refresh Status
        </button>
      </div>
    </div>
  );
};

export default POSReaderStatus;
